import { roundToNext5MinuteTick } from "@src/appV2/lib/utils";
import { addMinutes, isEqual, max } from "date-fns";

interface ComputeTargetArrivalTimeParams {
  shiftStart: Date;
  etaInMinutes: number;
  gracePeriodInMinutes: number;
  shiftAuctionEndDate?: Date;
}

/**
 * Computes the target arrival time based on the shift start time and ETA.
 *
 * Logic:
 * 1. If no ETA is provided, returns the shift start time
 * 2. If ETA is provided, calculates: current time + ETA + grace period
 * 3. Returns the maximum of shift start time and ETA with buffer
 * 4. If ETA with buffer is greater than shift start, rounds to next 5-minute tick
 *
 * This ensures the target arrival time is never earlier than the shift start time
 * and accounts for travel time with a safety buffer, rounded to convenient 5-minute intervals.
 */
export function computeTargetArrivalTime(params: ComputeTargetArrivalTimeParams): Date {
  const { shiftStart, etaInMinutes, gracePeriodInMinutes, shiftAuctionEndDate } = params;

  const referenceDate = shiftAuctionEndDate ?? new Date();
  const etaWithBuffer = addMinutes(referenceDate, etaInMinutes + gracePeriodInMinutes);
  const maxTime = max([shiftStart, etaWithBuffer]);

  // If the max time is shift start then don't round
  if (isEqual(maxTime, shiftStart)) {
    return shiftStart;
  }

  // else round to next 5-minute tick
  return roundToNext5MinuteTick(maxTime);
}
